import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const typographyVariants = cva("", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
      "4xl": "text-4xl",
      "5xl": "text-5xl",
    },
    weight: {
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    emphasis: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      destructive: "text-destructive",
      primary: "text-primary",
    },
    tracking: {
      tight: "tracking-tight",
      normal: "tracking-normal",
      wide: "tracking-wide",
    },
    leading: {
      none: "leading-none",
      tight: "leading-tight",
      normal: "leading-normal",
      relaxed: "leading-relaxed",
    },
    decoration: {
      none: "no-underline",
      underline: "underline",
      "underline-offset": "underline underline-offset-4",
      lineThrough: "line-through",
    },
    italic: {
      true: "italic",
      false: "",
    },
    mono: {
      true: "font-mono",
      false: "",
    },
    balance: {
      true: "text-balance",
      false: "",
    },
    smallCaps: {
      true: "font-variant-small-caps first-letter:uppercase first-letter:font-light lowercase",
      false: "",
    },
    border: {
      none: "",
      bottom: "border-b",
    },
    family: {
      sans: "font-sans",
      serif: "font-serif",
      times: "font-times",
    },
  },
  defaultVariants: {
    decoration: "none",
    italic: false,
    mono: false,
    balance: false,
    smallCaps: false,
    border: "none",
    family: "serif",
  },
});

type TypographyElement = React.ElementType;

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: TypographyElement;
  asChild?: boolean;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      as,
      asChild = false,
      size,
      weight,
      emphasis,
      tracking,
      leading,
      decoration,
      italic,
      mono,
      balance,
      smallCaps,
      border,
      className,
      family,
      ...props
    },
    ref,
  ) => {
    const Comp: React.ElementType = asChild ? Slot : (as ?? "p");

    return (
      <Comp
        ref={ref}
        className={cn(
          typographyVariants({
            size,
            weight,
            emphasis,
            tracking,
            leading,
            decoration,
            italic,
            mono,
            balance,
            smallCaps,
            border,
            family,
          }),
          className,
        )}
        {...props}
      />
    );
  },
);
Typography.displayName = "Typography";

export { Typography, typographyVariants };
